import { CollectDataConfig, CollectDataStep } from 'src/interfaces/collectData'
import { State } from '../reducers/types'
import {
  clientSortSteps,
  backendSortSteps,
  sortByDisabled,
  sortByCreated,
  filterDeprecatedCollectDataSteps,
  filterReplacedFailedSteps,
  sortRecollectionSteps,
  getDeactivationDeadlineDays,
} from 'src/utils/collectData'
import {
  CollectDataConfigStatus,
  CollectDataStepStatus,
  CollectDataStepType,
} from 'src/constants/collect-data/enums'
import { COLLECT_DATA_STEP_TIME_TO_COMPLETE } from 'src/constants/collect-data/constants'
import { DataCollectionSource } from 'src/constants/shared'

const isCollectedDataConfigStatusCreated = (collectDataConfig: CollectDataConfig) => {
  return (
    collectDataConfig.status === CollectDataConfigStatus.Created &&
    collectDataConfig.collectDataSteps.some((item) => item.status === CollectDataStepStatus.Created)
  )
}

const getVisibleConfigStatus = (
  configStatus: CollectDataConfigStatus,
  steps: CollectDataStep[],
): CollectDataConfigStatus => {
  const isEveryStepDone = steps.every((step) => {
    return (
      step.status !== CollectDataStepStatus.Created && step.status !== CollectDataStepStatus.Failed
    )
  })

  if (isEveryStepDone) {
    return CollectDataConfigStatus.Completed
  }

  // backend keeps the config failed until the recollected failed step is archived
  const hasFailedStep = steps.some((step) => {
    return step.status === CollectDataStepStatus.Failed
  })

  if (configStatus === CollectDataConfigStatus.Failed && !hasFailedStep) {
    return CollectDataConfigStatus.Created
  }

  return configStatus
}

export const getCollectDataStepConfigs = (state: State): CollectDataConfig[] => {
  return sortByDisabled(
    (
      state.driver.driver?.collectDataStepsConfigs
        .sort((a, b) => a.order - b.order)
        .map((item) => {
          // filter steps for recollection flow to only show created steps
          const visibleSteps = filterReplacedFailedSteps(
            filterDeprecatedCollectDataSteps(item.collectDataSteps),
          )
          const steps =
            state.driver.driver?.collectDataFlow === DataCollectionSource.Recollection
              ? visibleSteps.filter((step) => step.status === CollectDataStepStatus.Created)
              : // in onboarding if we have same step with created and completed status we show only created step
                visibleSteps.filter((step) => {
                  const sameStepWithCreatedStatusExists = visibleSteps.some((sameStep) => {
                    return (
                      sameStep.type === step.type &&
                      sameStep.label === step.label &&
                      sameStep.status === CollectDataStepStatus.Created
                    )
                  })

                  return (
                    step.status !== CollectDataStepStatus.Completed ||
                    !sameStepWithCreatedStatusExists
                  )
                })

          return {
            ...item,
            status: getVisibleConfigStatus(item.status, steps),
            collectDataSteps: sortByCreated(
              steps.every((item) => item.order === 0)
                ? clientSortSteps(steps)
                : backendSortSteps(steps),
            ),
          }
        }) || []
    ).filter((item) => !!item.collectDataSteps.length),
  )
}

export const getCurrentCollectDataStepConfig = (state: State): CollectDataConfig | null => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return stepConfigs.find(isCollectedDataConfigStatusCreated) || null
}

export const getCurrentCollectDataStepConfigIndex = (state: State): number => {
  const stepConfigs = getCollectDataStepConfigs(state)

  const index = stepConfigs.findIndex(isCollectedDataConfigStatusCreated)

  return index >= 0 ? index + 1 : 0
}

export const getCollectDataSteps = (state: State): CollectDataStep[] =>
  getCollectDataStepConfigs(state)
    .map((item) => item.collectDataSteps)
    .flat()
    .filter((item) => item.status !== CollectDataStepStatus.Archived) || []

const getAllRecollectionSteps = (state: State): CollectDataStep[] => {
  return (state.driver.driver?.collectDataStepsConfigs || [])
    .flatMap((config) => {
      return filterDeprecatedCollectDataSteps(config.collectDataSteps)
    })
    .filter((step) => {
      return step.status !== CollectDataStepStatus.Archived
    })
}

export const getCurrentCollectDataStep = (state: State): CollectDataStep | null => {
  if (state.driver.driver?.collectDataFlow === DataCollectionSource.Recollection) {
    const selectedStepId = state.recollection.selectedStepId

    return getAllRecollectionSteps(state).find((item) => item.id === selectedStepId) || null
  }

  const steps = getCollectDataSteps(state)

  return steps.find((item) => item.status === CollectDataStepStatus.Created) || null
}

export const getOnlyDelayedStepsLeft = (state: State): boolean => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return (
    stepConfigs.every(
      (item) =>
        item.status !== CollectDataConfigStatus.Created ||
        (item.disabled === true && isCollectedDataConfigStatusCreated(item)),
    ) && stepConfigs.some((item) => isCollectedDataConfigStatusCreated(item))
  )
}

export const getCollectDataStepsRemainingTimeToComplete = (state: State): number => {
  const steps = getCollectDataSteps(state)

  return steps.reduce(
    (res, item) =>
      (res +=
        (item.status === CollectDataStepStatus.Created &&
          COLLECT_DATA_STEP_TIME_TO_COMPLETE[item.type]) ||
        0),
    0,
  )
}

export const getCollectDataStepIndex =
  (stepId: string | undefined) =>
  (state: State): number => {
    const steps = getCollectDataSteps(state)
    const index = steps.findIndex((item) => item.id === stepId)

    return index >= 0 ? index + 1 : 0
  }

export const getCollectDataStepIndexInConfig =
  (stepId: string | undefined) =>
  (state: State): number => {
    const stepConfigs = getCollectDataStepConfigs(state)

    let res = 0
    stepConfigs.find((item) => {
      const stepIndex = item.collectDataSteps.findIndex((step) => step.id === stepId)

      if (stepIndex >= 0) {
        res = stepIndex + 1
      }
    })

    return res
  }

export const getCollectDataStepsCount = (state: State): number => getCollectDataSteps(state).length

export const getStepByType =
  <T>(stepType: CollectDataStepType) =>
  (state: State): T | null => {
    const steps = getCollectDataSteps(state)

    return (steps.find((item) => item.type === stepType) as T) || null
  }

export const getIsCollectDataStarted = (state: State): boolean => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return stepConfigs.some((item) => !isCollectedDataConfigStatusCreated(item))
}

export const getIsCollectDataFinished = (state: State): boolean => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return stepConfigs.every((item) => !isCollectedDataConfigStatusCreated(item))
}

// read raw configs: the recollection flow hides failed steps from getCollectDataStepConfigs
export const getIsCollectDataFailed = (state: State): boolean => {
  const stepConfigs = state.driver.driver?.collectDataStepsConfigs || []

  return stepConfigs.some((config) => {
    const steps = filterReplacedFailedSteps(
      filterDeprecatedCollectDataSteps(config.collectDataSteps),
    )

    return steps.some((step) => {
      return step.status === CollectDataStepStatus.Failed
    })
  })
}

export const getRecollectionActionNeededSteps = (state: State): CollectDataStep[] => {
  return sortRecollectionSteps(
    getAllRecollectionSteps(state).filter((step) => {
      return step.status === CollectDataStepStatus.Created
    }),
  )
}

export const getRecollectionUnderReviewSteps = (state: State): CollectDataStep[] => {
  return getAllRecollectionSteps(state).filter((step) => {
    return (
      step.status === CollectDataStepStatus.WaitingForApproval ||
      step.status === CollectDataStepStatus.WaitingForInternalApproval
    )
  })
}

export const getRecollectionDeactivationDeadlineDays = (state: State): number | null => {
  return getDeactivationDeadlineDays(getRecollectionActionNeededSteps(state))
}
